import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Phone, Mail, Eye, EyeOff, RotateCw, Copy, MessageCircle, Power } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, getErr, safeCopy, toWaNumber } from "../../lib/api";

export default function AdminOperators() {
  const [operators, setOperators] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [creds, setCreds] = useState({}); // { opId: { password, login_id, mobile, visible } }

  const load = () => api.get("/admin/operators").then((r) => setOperators(r.data.operators || []));
  useEffect(() => { load(); }, []);

  const resetForm = () => setForm({ name: "", email: "", phone: "", password: "" });

  const onCreate = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password are required");
    try {
      await api.post("/admin/operators", form);
      toast.success("Operator created");
      setCreateOpen(false);
      resetForm();
      load();
    } catch (e) { toast.error(getErr(e, "Failed to create operator")); }
  };

  const onSaveEdit = async () => {
    try {
      const update = { name: editing.name, phone: editing.phone };
      if (editing.password) update.password = editing.password;
      await api.patch(`/admin/operators/${editing.id}`, update);
      toast.success("Operator updated");
      setEditing(null);
      // refresh credentials cache too
      setCreds((prev) => ({ ...prev, [editing.id]: undefined }));
      load();
    } catch (e) { toast.error(getErr(e, "Failed to update operator")); }
  };

  const onDelete = async (op) => {
    if (!window.confirm(`Remove ${op.name}? Their previous assignments will keep their name reference.`)) return;
    try {
      await api.delete(`/admin/operators/${op.id}`);
      toast.success("Operator removed");
      load();
    } catch { toast.error("Failed to remove"); }
  };

  const onToggleActive = async (op) => {
    const verb = op.is_active ? "deactivate" : "activate";
    if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${op.name}? ${op.is_active ? "They will not be able to log in." : "They will regain access."}`)) return;
    try {
      const { data } = await api.post(`/admin/operators/${op.id}/toggle-active`);
      // Optimistic local update so the pill flips immediately, then refetch for canonical state.
      setOperators((prev) => prev.map((o) => (o.id === op.id ? { ...o, is_active: data.is_active } : o)));
      toast.success(`${op.name} is now ${data.is_active ? "Active" : "Disabled"}`);
      load();
    } catch (e) { toast.error(getErr(e, "Failed to toggle status")); }
  };

  const onReveal = async (op) => {
    if (creds[op.id]?.password !== undefined) {
      setCreds((p) => ({ ...p, [op.id]: { ...p[op.id], visible: !p[op.id].visible } }));
      return;
    }
    try {
      const { data } = await api.get(`/admin/operators/${op.id}/credentials`);
      setCreds((p) => ({ ...p, [op.id]: { ...data, visible: true } }));
    } catch (e) { toast.error(getErr(e, "Could not load credentials")); }
  };

  const onResetPassword = async (op) => {
    if (!window.confirm(`Reset password for ${op.name}? The current password will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/operators/${op.id}/reset-password`);
      setCreds((p) => ({ ...p, [op.id]: { password: data.password, login_id: data.login_id, mobile: op.mobile || op.phone || "", visible: true } }));
      toast.success(`New password: ${data.password}`);
    } catch (e) { toast.error(getErr(e, "Failed to reset password")); }
  };

  const onCopyPw = async (op) => {
    const pw = creds[op.id]?.password;
    if (!pw) return toast.error("Reveal password first");
    const ok = await safeCopy(pw);
    if (ok) toast.success("Password copied");
    else toast.error("Could not copy — long-press to copy manually");
  };

  const onWhatsAppShare = (op) => {
    const c = creds[op.id];
    if (!c?.password) return toast.error("Reveal password first");
    const mobile = c.mobile || op.mobile || op.phone || "";
    const wa = toWaNumber(mobile);
    if (!wa || wa.length < 10) return toast.error("Operator has no mobile number set");
    const msg = `Hi ${op.name}, welcome to FinSahayak CRM.\n\nYour login details:\nLogin ID: ${c.login_id}\nPassword: ${c.password}\n\nLogin at: ${window.location.origin}/login`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-operators-page">
      <Toaster richColors position="top-right" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="label-eyebrow">Team</span>
          <h1 className="mt-2 text-3xl font-display font-medium tracking-tight text-[#121C17]">Operators <span className="text-[#5C6661] text-base ml-1">({operators.length})</span></h1>
          <p className="text-sm text-[#5C6661] mt-2 max-w-xl">Create login accounts for your team. Reveal credentials, share via WhatsApp, reset passwords, or deactivate access — all from one place.</p>
        </div>
        <button onClick={() => setCreateOpen((v) => !v)} data-testid="operators-create-toggle"
          className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} /> {createOpen ? "Cancel" : "Add operator"}
        </button>
      </div>

      {createOpen && (
        <div className="bento-card p-6 mb-6" data-testid="operators-create-form">
          <h3 className="label-eyebrow text-[10px] mb-4">Add new operator</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name *" v={form.name} on={(v) => setForm({ ...form, name: v })} test="op-form-name" />
            <Field label="Email *" v={form.email} on={(v) => setForm({ ...form, email: v })} test="op-form-email" type="email" />
            <Field label="Mobile / Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} test="op-form-phone" placeholder="10-digit Indian mobile" />
            <Field label="Temporary password *" v={form.password} on={(v) => setForm({ ...form, password: v })} test="op-form-password" type="text" placeholder="Min 6 characters" />
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button onClick={() => { setCreateOpen(false); resetForm(); }} className="rounded-full px-5 py-2.5 text-sm font-medium border border-[#E5E2DC]">Cancel</button>
            <button onClick={onCreate} data-testid="op-form-submit" className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium">Create operator</button>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="operators-table">
        {operators.length === 0 ? (
          <div className="bento-card p-10 text-center text-sm text-[#5C6661]">No operators yet. Add your first team member above.</div>
        ) : (
          operators.map((op) => {
            const c = creds[op.id];
            const isEditing = editing?.id === op.id;
            return (
              <div key={op.id} className="bento-card p-5" data-testid={`operator-row-${op.id}`}>
                {isEditing ? (
                  <div className="grid sm:grid-cols-4 gap-3 items-end">
                    <Field label="Name" v={editing.name} on={(v) => setEditing({ ...editing, name: v })} test={`edit-name-${op.id}`} />
                    <div>
                      <label className="label-eyebrow text-[10px]">Email (read-only)</label>
                      <div className="mt-2 w-full bg-[#F3F1EC] border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm text-[#5C6661]">{op.email}</div>
                    </div>
                    <Field label="Phone" v={editing.phone || ""} on={(v) => setEditing({ ...editing, phone: v })} test={`edit-phone-${op.id}`} />
                    <Field label="New password (optional)" v={editing.password || ""} on={(v) => setEditing({ ...editing, password: v })} test={`edit-password-${op.id}`} placeholder="Leave blank to keep" />
                    <div className="sm:col-span-4 flex gap-2 justify-end">
                      <button onClick={() => setEditing(null)} className="rounded-full px-4 py-2 text-sm border border-[#E5E2DC] inline-flex items-center gap-1.5"><X size={14} /> Cancel</button>
                      <button onClick={onSaveEdit} data-testid={`save-${op.id}`} className="btn-primary rounded-full px-4 py-2 text-sm inline-flex items-center gap-1.5"><Save size={14} /> Save</button>
                    </div>
                  </div>
                ) : (
                  <div className="grid lg:grid-cols-12 gap-4 items-start">
                    <div className="lg:col-span-4 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-[#1E4D3B]/10 text-[#1E4D3B] grid place-items-center text-base font-medium font-display">
                        {op.name?.[0]?.toUpperCase() || "O"}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-medium text-[#121C17] truncate" data-testid={`op-name-${op.id}`}>{op.name}</div>
                        <div className="text-xs text-[#5C6661] mt-0.5 flex flex-wrap gap-3">
                          <a href={`mailto:${op.email}`} className="inline-flex items-center gap-1 hover:text-[#1E4D3B]"><Mail size={11} /> {op.email}</a>
                          {(op.phone || op.mobile) && (
                            <a href={`tel:${op.phone || op.mobile}`} className="inline-flex items-center gap-1 hover:text-[#1E4D3B]"><Phone size={11} /> {op.phone || op.mobile}</a>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-4 flex flex-col gap-2">
                      <span className="label-eyebrow text-[10px]">Portal credentials</span>
                      {!c ? (
                        <button onClick={() => onReveal(op)} data-testid={`op-reveal-${op.id}`}
                          className="inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-xs font-medium bg-[#F3F1EC] hover:bg-[#E5E2DC]">
                          <Eye size={12} /> Reveal password
                        </button>
                      ) : (
                        <div className="bg-[#F3F1EC] rounded-xl px-3 py-2 text-xs font-mono break-all flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`op-creds-${op.id}`}>
                          <span className="text-[#5C6661] not-italic">ID:</span> <span data-testid={`op-login-${op.id}`}>{c.login_id}</span>
                          <span className="text-[#5C6661]">·</span>
                          <span className="text-[#5C6661]">PW:</span>
                          <span data-testid={`op-pw-${op.id}`}>{c.visible ? (c.password || "—") : "••••••••"}</span>
                          <div className="ml-auto flex items-center gap-1">
                            <button onClick={() => setCreds((p) => ({ ...p, [op.id]: { ...c, visible: !c.visible } }))} className="p-1 hover:bg-white rounded" title={c.visible ? "Mask" : "Show"} data-testid={`op-toggle-${op.id}`}>
                              {c.visible ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                            <button onClick={() => onCopyPw(op)} className="p-1 hover:bg-white rounded" title="Copy" data-testid={`op-copy-${op.id}`}><Copy size={12} /></button>
                            <button onClick={() => onWhatsAppShare(op)} className="p-1 hover:bg-white rounded text-emerald-700" title="Share on WhatsApp" data-testid={`op-wa-${op.id}`}><MessageCircle size={12} /></button>
                            <button onClick={() => onResetPassword(op)} className="p-1 hover:bg-white rounded text-amber-700" title="Reset password" data-testid={`op-reset-${op.id}`}><RotateCw size={12} /></button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="lg:col-span-2">
                      <span className="label-eyebrow text-[10px]">Status</span>
                      <div className="mt-1">
                        <span className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${op.is_active ? "pill-completed" : "pill-rejected"}`} data-testid={`op-status-${op.id}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${op.is_active ? "bg-emerald-600" : "bg-red-500"}`}></span>
                          {op.is_active ? "Active" : "Disabled"}
                        </span>
                      </div>
                    </div>

                    <div className="lg:col-span-2 flex gap-2 lg:justify-end flex-wrap">
                      <button onClick={() => onToggleActive(op)} data-testid={`op-toggle-active-${op.id}`}
                        className={`rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 border ${op.is_active ? "border-red-200 text-red-700 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                        <Power size={12} /> {op.is_active ? "Deactivate" : "Activate"}
                      </button>
                      <button onClick={() => setEditing({ ...op, password: "" })} data-testid={`edit-${op.id}`}
                        className="rounded-full p-2 border border-[#E5E2DC] hover:bg-[#F3F1EC]" aria-label="Edit"><Pencil size={14} /></button>
                      <button onClick={() => onDelete(op)} data-testid={`delete-${op.id}`}
                        className="rounded-full p-2 border border-red-100 text-red-600 hover:bg-red-50" aria-label="Delete"><Trash2 size={14} /></button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const Field = ({ label, v, on, test, type = "text", placeholder }) => (
  <div>
    <label className="label-eyebrow text-[10px]">{label}</label>
    <input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={test}
      className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]" />
  </div>
);
